import { NetworkStatus } from '@apollo/client';
import { ReactComponent as AsLogo } from '@bechtel/client-assets/dist/images/as-unauthorized-logo.svg';
import { ReactComponent as PfLogo } from '@bechtel/client-assets/dist/images/pf-unauthorized-logo.svg';
import { ReactComponent as Unauthorized } from '@bechtel/client-assets/dist/images/unauthorized.svg';
import { Box, Separator, SeparatorType, Spinner, SpinnerSize, Text, theme } from '@bechtel/gds';
import { Trans } from '@lingui/macro';
import { Fragment, useEffect } from 'react';
import { Navigate, useOutlet } from 'react-router-dom';
import { ErrorRetry } from '~/components/atoms/error-retry';
import { useEnvProvider } from '~/hooks';
import { useAuthorizeUser } from '~/hooks/use-authorize-user';
import { useSessionStorage } from '~/hooks/use-session-storage';
import { setUnauthenticated } from '~/utilities/auth';

export const AuthRoute = () => {
  const outlet = useOutlet();
  const [authenticated] = useSessionStorage('authenticated');
  const [getUserByAuth, { loading, error, data, refetch, networkStatus, called }] = useAuthorizeUser();
  const { envConfig }: { envConfig: Record<string, string> } = useEnvProvider();

  useEffect(() => {
    if (!authenticated || called) return;
    getUserByAuth();
  });

  if (!authenticated) {
    setUnauthenticated();
    return <Navigate to="/auth/request" replace />;
  } else {
    if (!called) getUserByAuth();
    if (loading || networkStatus === NetworkStatus.refetch)
      return (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
          data-testid="loading"
        >
          <Spinner size={SpinnerSize.LARGE}></Spinner>
          <Text color="blue.800" size="600">
            <Trans>Authorizing</Trans>
          </Text>
        </Box>
      );
  }
  if (!data && called) {
    {
      return (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
          data-testid="loading"
        >
          <Spinner size={SpinnerSize.LARGE}></Spinner>
          <Text color="blue.800" size="600">
            <Trans>Loading...</Trans>
          </Text>
        </Box>
      );
    }
  }
  if (!loading && error)
    return (
      <Box>
        <ErrorRetry refetch={refetch}>
          <Trans> There was error loading user details. Please click the button below to try again.</Trans>
        </ErrorRetry>
      </Box>
    );
  if (called && error)
    return (
      <Box
        alignItems={{ sm: 'center' }}
        flexDirection={{ sm: 'column' }}
        pt={{ sm: '900' }}
        css={{ textAlign: 'center' }}
      >
        <Unauthorized
          style={{
            width: '268px',
            height: ' 296px',
          }}
        />
        <Text size="500" color="black" family={'graphik'} style={{ marginTop: theme.spacings[600] }}>
          <Trans>You might not have access! Please reach out to the</Trans>{' '}
          <a
            href={`mailto:${envConfig.adminEmail}`}
            style={{
              color: theme.colors.blue[600] as string,
            }}
          >
            <Trans>admin</Trans>
          </a>{' '}
          <Trans>for any information.</Trans>
        </Text>
        <Separator
          type={SeparatorType.HORIZONTAL}
          spacing={'900'}
          borderWidth="1px"
          size="50%"
          style={{ backgroundColor: theme.colors.grey[300] as string }}
        />
        {envConfig.appName === 'PF' ? (
          <PfLogo
            style={{
              width: '179px',
              height: '33px',
            }}
          />
        ) : (
          <AsLogo
            style={{
              width: '179px',
              height: '33px',
            }}
          />
        )}
      </Box>
    );
  return <Fragment>{outlet}</Fragment>;
};
